import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Service, GalleryServiceWithDetails } from '../types'
import {
  fetchServices,
  fetchGalleryServices,
  addServiceToGallery,
  removeServiceFromGallery,
  setPrimaryService,
  updateGalleryServices,
  createService
} from '../api/servicesApi'

/**
 * Services hooks for gallery categorization.
 *
 * @see servicesApi.ts - CRUD operations for services
 * @see supabase/migrations/20251204_create_services_table.sql - Database schema
 * @see supabase/migrations/20251204_create_gallery_services_table.sql - Junction table
 */

/**
 * Fetch all available services for filter dropdowns and selectors.
 * Results are cached for 5 minutes to reduce API calls.
 */
export function useServices() {
  return useQuery<Service[]>({
    queryKey: ['services'],
    queryFn: fetchServices,
    staleTime: 5 * 60_000 // Cache for 5 minutes
  })
}

/**
 * Fetch services linked to a specific gallery.
 *
 * @param galleryId - The gallery ID to fetch services for
 */
export function useGalleryServices(galleryId: number) {
  return useQuery<GalleryServiceWithDetails[]>({
    queryKey: ['gallery-services', galleryId],
    queryFn: () => fetchGalleryServices(galleryId),
    enabled: !!galleryId // Only fetch when galleryId is valid
  })
}

/**
 * Create a new service (admin feature).
 * Invalidates the services cache on success.
 *
 * @note Uses `title` field to match existing services table schema.
 * @see servicesApi.ts - createService() expects title, not name
 */
export function useCreateService() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ title, description }: { title: string; description?: string }) =>
      createService(title, description),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['services'] })
    }
  })
}

/**
 * Mutations for managing gallery-service relationships.
 *
 * @param galleryId - The gallery ID to manage services for
 * @returns Object with add, remove, setPrimary, and bulkUpdate mutations
 */
export function useServiceRelations(galleryId: number) {
  const qc = useQueryClient()

  const invalidateRelated = () => {
    qc.invalidateQueries({ queryKey: ['gallery-services', galleryId] })
    qc.invalidateQueries({ queryKey: ['gallery', galleryId] })
    qc.invalidateQueries({ queryKey: ['galleries'] })
  }

  /**
   * Add a service to the gallery
   */
  const add = useMutation({
    mutationFn: ({
      serviceId,
      isPrimary = false
    }: {
      serviceId: string
      isPrimary?: boolean
    }) => addServiceToGallery(galleryId, serviceId, isPrimary),
    onSuccess: invalidateRelated
  })

  /**
   * Remove a service from the gallery
   */
  const remove = useMutation({
    mutationFn: ({ serviceId }: { serviceId: string }) =>
      removeServiceFromGallery(galleryId, serviceId),
    onSuccess: invalidateRelated
  })

  /**
   * Set a service as the primary service for this gallery.
   * The database trigger will automatically unset any existing primary.
   */
  const setPrimary = useMutation({
    mutationFn: ({ serviceId }: { serviceId: string }) =>
      setPrimaryService(galleryId, serviceId),
    onSuccess: invalidateRelated
  })

  /**
   * Bulk update all services for a gallery.
   * Replaces existing associations with the provided list.
   */
  const bulkUpdate = useMutation({
    mutationFn: ({
      serviceIds,
      primaryServiceId
    }: {
      serviceIds: string[]
      primaryServiceId?: string
    }) => updateGalleryServices(galleryId, serviceIds, primaryServiceId),
    onSuccess: invalidateRelated
  })

  return { add, remove, setPrimary, bulkUpdate }
}
